import { PlusIcon } from '@phosphor-icons/react';
import { Breadcrumb } from 'components/Breadcrumb';
import { Button } from 'components/button/Button';
import { PageHeader } from 'components/PageHeader';
import { TextAreaField } from 'components/textarea/TextAreaField';
import { FormikConsumer, FormikProvider } from 'formik';
import { useFormik } from 'components/form/formik';
import { OrderLineStopType, TrailerType, LoadingMethod } from 'src/types/enums';
import { bulkDuplicateOrder, createOrder, useOrderById, type OrderDetail } from '../../models';
import { DelayedSpinner } from 'components/Spinner';
import { useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invariant, nullthrows } from '@utils/invariant';
import * as Yup from 'yup';

import { InputField } from '../../../../components/input/InputField';
import { parseInputTime } from '../../../../utils/date';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { Customer, CustomerComboboxField } from '../../../customer/CustomerComboboxField';
import { Supplier, SupplierComboboxField } from '../../../supplier/SupplierComboboxField';
import { CreateOrderLineFields, ICreateOrderLineValues } from '../orderLine/CreateOrderLineFields';
import { createOrderLineContentSchema } from '../orderLineContent/CreateOrderLineContent';
import { createOrderLineSaleSchema } from '../orderLineSale/CreateOrderLineSale';
import { createOrderPurchaseSchema } from '../orderPurchase/CreateOrderPurchase';
import { createOrderLineStopSchema, initialValues as initialStopValues } from '../orderLineStop/CreateOrderLineStop';
import { STOP_TYPE_ITEMS } from '../orderLineStop/constants';
import { CheckboxField } from '../../../../components/checkbox/CheckboxField';
import { TrailerTypes } from './TrailerTypes';
import { LoadingMethods } from './LoadingMethods';
import { TrailerComboboxField } from '../../../trailer/TrailerComboboxField';
import type { TrailerOption } from '../../../trailer/displayTrailer';
import { cargoTypeToComboboxItem } from '../../cargoType/CargoTypeComboboxField';
import { packageTypeToComboboxItem } from '../../packageType/PackageTypeComboboxField';
import { productTypeToComboboxItem } from '../../productType/ProductTypeComboboxField';
import { vatRateToComboboxItem } from '../../vatRate/VatRateComboboxField';
import { PageHeading } from 'components/PageHeading';
import { Card } from 'components/Card';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';
import { useTranslation } from '../../../../contexts/translation-context';

const createOrderSchema = Yup.object().shape({
  customer: Yup.mixed().nullable().required('Vereist'),
  supplier: Yup.mixed().nullable(),
  trailer: Yup.mixed().nullable(),
  customerRef: Yup.string(),
  internalNotes: Yup.string(),
  externalNotes: Yup.string(),
  allowedTrailerTypes: Yup.array(),
  loadingMethod: Yup.mixed().nullable(),
  requiresAnonymousCMR: Yup.boolean().required('Vereist'),
  orderCount: Yup.number().min(1, 'Minimaal 1').max(100, 'Maximaal 100').integer('Moet een geheel getal zijn'),
  skipDraft: Yup.boolean(),

  stops: Yup.array(createOrderLineStopSchema).required('Vereist'),
  contents: Yup.array(createOrderLineContentSchema).required('Vereist'),
  purchases: Yup.array(createOrderPurchaseSchema).required('Vereist'),
  sales: Yup.array(createOrderLineSaleSchema).required('Vereist'),
});

interface IOrderValues extends ICreateOrderLineValues {
  customer: Customer | null;
  supplier: Supplier | null;
  /** The trailer the order starts on; the planning board overwrites it when the order is planned. */
  trailer: TrailerOption | null;
  customerRef: string;
  internalNotes: string;
  externalNotes: string;
  requiresAnonymousCMR: boolean;
  noPurchase: boolean;
  allowedTrailerTypes: TrailerType[];
  loadingMethod: LoadingMethod | null;
  orderCount: number;
  skipDraft: boolean;
}

function parseBaseRef(ref: string): number | null {
  const match = ref.match(/^0*(\d+)$/);
  if (!match) return null;
  return parseInt(match[1]!, 10);
}

function ReferencePreview({ customerRef, orderCount }: { customerRef: string; orderCount: number }) {
  if (orderCount <= 1 || !customerRef) return null;

  const baseNum = parseBaseRef(customerRef);
  if (baseNum === null) return null;

  const count = Number(orderCount);
  const first = baseNum;
  const last = baseNum + count - 1;

  return (
    <div className="text-sm text-fg-muted p-2 bg-surface-inverse rounded">
      Referenties: <span className="font-medium">{first}</span> t/m <span className="font-medium">{last}</span>
    </div>
  );
}

const CreateOrderPage = () => {
  const [searchParams] = useSearchParams();
  const copyFrom = searchParams.get('copy-from');
  // The Formik initialValues below are only read once, on mount, so when
  // copying from an existing order we must wait for the prefill data to be
  // ready before mounting the form — otherwise Formik would initialize with
  // empty defaults and never pick up the prefill once it arrives.
  const { data, isLoading } = useOrderById(copyFrom);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  return <CreateOrderForm copyFromOrder={data ?? null} />;
};

const CreateOrderForm: React.FC<{ copyFromOrder: OrderDetail | null }> = ({ copyFromOrder }) => {
  const navigate = useNavigate();
  const { lang } = useTranslation();

  const initialValues: IOrderValues = useMemo(() => {
    if (copyFromOrder) {
      const order = copyFromOrder;
      if (order.lines.length > 0) {
        const line = order.lines[0]!;
        return {
          customer: (order.customer ?? null) as Customer | null,
          supplier: null,
          customerRef: '',
          internalNotes: order.internalNotes,
          externalNotes: '',
          requiresAnonymousCMR: order.requiresAnonymousCMR,
          noPurchase: order.noPurchase,
          allowedTrailerTypes: order.allowedTrailerTypes as TrailerType[],
          loadingMethod: (order.loadingMethod ?? null) as LoadingMethod | null,
          // Deliberately not copied: which trailer an order is on is decided on the board, not copied with
          // the order it was duplicated from.
          trailer: null,
          orderCount: 1,
          skipDraft: false,
          stops: line.stops
            .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
            .map((stop) => ({
              type: STOP_TYPE_ITEMS.find((v) => v.key === stop.type)!,
              location: stop.location,
              date: '',
              timeStart: '',
              timeEnd: '',
              reference: '',
              notes: '',
            })),
          contents: line.contents.map((content) => ({
            cargoType: cargoTypeToComboboxItem(content.cargoType, lang),
            packages: decimateAndFormatNumber(content.packages, 2),
            packageType: packageTypeToComboboxItem(content.packageType, lang),
            weight: decimateAndFormatNumber(content.weight, 2),
            volume: decimateAndFormatNumber(content.volume, 2),
            loadingMeters: decimateAndFormatNumber(content.loadingMeters, 2),
          })),
          purchases: [],
          sales: line.sales.map((sale) => ({
            productType: productTypeToComboboxItem(sale.productType),
            vatRate: vatRateToComboboxItem(sale.vatRate),
            amount: decimateAndFormatNumber(sale.amount, 2),
            unitPrice: decimateAndFormatNumber(sale.unitPrice, 2),
            externalNote: '',
          })),
        };
      }
    }

    return {
      customer: null,
      supplier: null,
      trailer: null,
      customerRef: '',
      internalNotes: '',
      externalNotes: '',
      requiresAnonymousCMR: false,
      noPurchase: false,
      allowedTrailerTypes: [],
      loadingMethod: null,
      orderCount: 1,
      skipDraft: false,
      stops: [
        {
          ...initialStopValues,
          type: STOP_TYPE_ITEMS.find((item) => item.key === OrderLineStopType.Load)!,
        },
      ],
      contents: [],
      purchases: [],
      sales: [],
    };
  }, [copyFromOrder, lang]);

  const formikbag = useFormik({
    initialValues: initialValues,
    validationSchema: createOrderSchema,
    onSubmit: async (values) => {
      try {
        const {
          noPurchase,
          customer,
          supplier,
          customerRef,
          internalNotes,
          externalNotes,
          requiresAnonymousCMR,

          stops,
          contents,
          purchases,
          sales,
          allowedTrailerTypes,
          loadingMethod,
          trailer,
          orderCount,
          skipDraft,
        } = values;
        const customerId = customer?.id;
        invariant(customerId);
        const supplierId = supplier?.id;

        const isBulk = orderCount > 1;
        const baseNum = isBulk ? parseBaseRef(customerRef) : null;
        if (isBulk && baseNum === null) {
          toast.error('Factuurreferentie moet numeriek zijn bij meerdere orders');
          return;
        }

        const firstRef = isBulk ? String(baseNum!) : customerRef;

        const orderData = {
          noPurchase,
          customerId,
          supplierId,
          customerRef: firstRef,
          internalNotes,
          externalNotes,
          requiresAnonymousCMR,
          allowedTrailerTypes,
          loadingMethod,
          trailerId: trailer?.id ?? null,
          lines: [
            {
              stops: stops.map((stop, i) => {
                const { type: selectedType, location, date, timeStart, timeEnd, reference, notes } = stop;
                const locationId = location?.id;
                invariant(locationId);
                const type = nullthrows(selectedType?.key, 'Stop type is required');

                return {
                  sequenceIndex: i * 10,
                  type: type as OrderLineStopType,
                  locationId,
                  date,
                  timeStart: parseInputTime(timeStart),
                  timeEnd: parseInputTime(timeEnd),
                  reference,
                  notes,
                };
              }),

              contents: contents.map((content) => {
                const { cargoType, packageType, packages, weight, volume, loadingMeters } = content;
                const cargoTypeId = cargoType?.key;
                const packageTypeId = packageType?.key;
                invariant(cargoTypeId);
                invariant(packageTypeId);

                return {
                  cargoTypeId,
                  packageTypeId,
                  packages: parseNumberInput(packages, 2),
                  weight: parseNumberInput(weight, 2),
                  volume: parseNumberInput(volume, 2),
                  loadingMeters: parseNumberInput(loadingMeters, 2),
                };
              }),
              purchases: purchases.map((purchase) => {
                const { productType, vatRate, amount, unitPrice, externalNote } = purchase;
                const productTypeId = nullthrows(productType?.key, 'Product type is vereist');
                const vatRateId = nullthrows(vatRate?.key, 'Product type is vereist');
                return {
                  productTypeId,
                  vatRateId,
                  amount: parseNumberInput(amount, 2),
                  unitPrice: parseNumberInput(unitPrice, 2),
                  externalNote: externalNote ?? '',
                };
              }),
              sales: sales.map((sale) => {
                const { productType, vatRate, amount, unitPrice, externalNote } = sale;
                const productTypeId = nullthrows(productType?.key, 'Product type is vereist');
                const vatRateId = nullthrows(vatRate?.key, 'Product type is vereist');
                return {
                  productTypeId,
                  vatRateId,
                  amount: parseNumberInput(amount, 2),
                  unitPrice: parseNumberInput(unitPrice, 2),
                  externalNote: externalNote ?? '',
                };
              }),
            },
          ],
        };

        const createdOrder = await createOrder({
          ...orderData,
          skipDraft: isBulk ? skipDraft : false,
        });

        if (isBulk && orderCount > 1) {
          const bulkResult = await bulkDuplicateOrder({
            orderId: createdOrder.id,
            count: orderCount - 1,
            baseInvoiceRef: firstRef,
            skipDraft,
          });

          const totalCreated = 1 + (bulkResult.orders?.length ?? 0);
          trackEvent('order_created', {
            order_id: createdOrder.id,
            customer_id: customerId,
            supplier_id: supplierId ?? null,
            bulk: true,
            order_count: totalCreated,
          });
          toast.success(`${totalCreated} orders aangemaakt`);
        } else {
          trackEvent('order_created', {
            order_id: createdOrder.id,
            customer_id: customerId,
            supplier_id: supplierId ?? null,
            bulk: false,
            order_count: 1,
          });
          toast.success('Order aangemaakt');
        }

        navigate(`../${createdOrder.id}/general`, { replace: true });
      } catch (err: any) {
        captureException(err);
        toast.error('Kon order niet aanmaken: ' + getDisplayError(err));
      }
    },
  });

  const customerValue = formikbag.values['customer'];
  useEffect(() => {
    const trailerTypes = formikbag.values['allowedTrailerTypes'];
    if (customerValue && customerValue.defaultTrailerTypes.length && !trailerTypes.length) {
      formikbag.setFieldValue('allowedTrailerTypes', customerValue.defaultTrailerTypes);
    }
  }, [customerValue]);

  return (
    <>
      <PageHeader title="Nieuwe order" />

      <div>
        <PageHeading
          leftSide={
            <Breadcrumb
              parentItem={{
                name: 'Orders',
                to: '/internal/orders',
              }}
              currentItem="Nieuwe order"
            />
          }
        />

        <div className="px-4">
          <FormikProvider value={formikbag}>
            <FormikConsumer>
              {({ handleSubmit, isSubmitting, values, errors, setFieldValue }) => {
                console.error(errors);
                return (
                  <form onSubmit={handleSubmit} className="grid gap-8">
                    <Card title="Standaard informatie">
                      <CustomerComboboxField name="customer" isDisabled={isSubmitting} />

                      <SupplierComboboxField name="supplier" isDisabled={isSubmitting} />

                      <TrailerComboboxField name="trailer" isDisabled={isSubmitting} />

                      <InputField
                        type="text"
                        labelText="Factuurreferentie klant"
                        name="customerRef"
                        isDisabled={isSubmitting}
                        helperText={
                          values.orderCount > 1
                            ? 'Numerieke basis. Referenties worden basis t/m basis+aantal-1.'
                            : undefined
                        }
                      />

                      <InputField type="number" labelText="Aantal orders" name="orderCount" isDisabled={isSubmitting} />

                      {values.orderCount > 1 && (
                        <div className="mb-8">
                          <CheckboxField labelText="Sla concept-status over (direct als Aangemaakt)" name="skipDraft" />
                          <ReferencePreview customerRef={values.customerRef} orderCount={values.orderCount} />
                        </div>
                      )}

                      <div className="grid grid-cols-2">
                        <CheckboxField labelText="Moet anonieme CMR aanmaken" name="requiresAnonymousCMR" />
                        <CheckboxField labelText="Geen aankopen" name="noPurchase" />
                      </div>
                    </Card>

                    <Card title="Trailer Types">
                      <TrailerTypes
                        value={values.allowedTrailerTypes}
                        onChange={(newTrailerTypes) => {
                          setFieldValue('allowedTrailerTypes', newTrailerTypes);
                        }}
                        isMultiSelect={true}
                      />
                    </Card>

                    <Card title="Wijze van belading">
                      <LoadingMethods
                        value={values.loadingMethod}
                        onChange={(newLoadingMethod) => {
                          setFieldValue('loadingMethod', newLoadingMethod);
                        }}
                      />
                    </Card>

                    <div>
                      <h2 className="section-header">Opdracht</h2>

                      <CreateOrderLineFields
                        noPurchase={values.noPurchase}
                        customer={values.customer}
                        supplier={values.supplier}
                        values={values}
                        isSubmitting={isSubmitting}
                      />
                    </div>

                    <Card title="Notities">
                      <TextAreaField
                        labelText="Interne notities"
                        name="internalNotes"
                        isDisabled={isSubmitting}
                        spellCheck={true}
                      />
                      <TextAreaField
                        labelText="Externe notities (voor vervoerder)"
                        name="externalNotes"
                        isDisabled={isSubmitting}
                        spellCheck={true}
                      />
                    </Card>

                    <div>
                      <Button
                        type="submit"
                        color="primary"
                        isDisabled={isSubmitting}
                        isLoading={isSubmitting}
                        iconLeft={<PlusIcon className="button-icon" />}
                      >
                        Maak order aan
                      </Button>
                    </div>
                  </form>
                );
              }}
            </FormikConsumer>
          </FormikProvider>
        </div>
      </div>
    </>
  );
};

export default CreateOrderPage;
